import type { ModuleId } from '@tahi/core';
import type { CommandEnv } from './commands/dispatch';

type Hook = (env: CommandEnv) => Promise<void>;

const hooks = new Map<ModuleId, Hook[]>();
/** Modules whose hooks have run successfully since they last became usable, for `doneFor`. */
const done = new Set<ModuleId>();
let doneFor: string | null = null;

/** Run `hook` each time `moduleId` becomes usable (toggle, admin forced_on, app start). Must be idempotent. */
export function onModuleUsable(moduleId: ModuleId, hook: Hook): () => void {
  hooks.set(moduleId, [...(hooks.get(moduleId) ?? []), hook]);
  return () =>
    hooks.set(
      moduleId,
      (hooks.get(moduleId) ?? []).filter((h) => h !== hook),
    );
}

/** Call with the full usable list every time it is recomputed. */
export async function noteUsableModules(
  usable: readonly ModuleId[],
  env: CommandEnv,
): Promise<void> {
  if (doneFor !== env.userId) {
    done.clear();
    doneFor = env.userId;
  }
  for (const id of done) if (!usable.includes(id)) done.delete(id);
  for (const id of usable) {
    if (done.has(id)) continue;
    try {
      for (const hook of hooks.get(id) ?? []) await hook(env);
      done.add(id);
    } catch (err) {
      console.warn(`module hook for ${id} failed; will retry`, err);
    }
  }
}

/** Tests only. */
export function resetModuleHooks(): void {
  hooks.clear();
  done.clear();
  doneFor = null;
}
